import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Base_Url } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';

const EditAdvancedServiceCard = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const editor = useRef(null);

    const [serviceData, setServiceData] = useState({
        title: '',
        items: [{ name: '', link: '' }],
        externalLink: '',
        externalLink: '',
        parentpath: '',
        icon: '',
        author: '',
        isHighlight: false,
        description: ''
    });

    const [content, setContent] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);

    // Fetch existing data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${Base_Url}/howto/${id}`);
                const data = response.data;
                
                setServiceData({
                    title: data.title || '',
                    link: data.link || '',
                    icon: data.icon || '',
                    intlink: data.intlink || '' 
                });
                
                setContent(data.pagedetails || '');
                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load service data');
                setLoading(false);
            }
        };
    
        if (id) {
            fetchData();
        }
    }, [id]);

    const editorConfig = useMemo(() => ({
        readonly: false,
        minHeight: 400,
        editHTMLDocumentMode: true,
        buttons: [
            'source', '|', 'bold', 'italic', 'underline', 'strikethrough', 'eraser', '|',
            'ul', 'ol', 'indent', 'outdent', '|', 'font', 'fontsize', 'paragraph',
            'lineHeight', 'brush', '|', 'superscript', 'subscript', 'classSpan', '|',
            'file', 'image', 'video', '|', 'spellcheck', 'speechRecognize', '|',
            'cut', 'copy', 'paste', 'selectall', 'copyformat', '|', 'hr', 'table',
            'link', 'symbols', '|', 'ai-commands', 'ai-assistant', '|', 'left', '|',
            'undo', 'redo', '|', 'find', 'fullsize', 'preview', 'print', 'about'
        ],
        plugins: [
            'add-new-line', 'ai-assistant', 'backspace', 'bold', 'class-span',
            'clean-html', 'clipboard', 'color', 'copyformat', 'delete-command',
            'drag-and-drop', 'drag-and-drop-element', 'dtd', 'enter', 'file',
            'focus', 'font', 'format-block', 'fullsize', 'hotkeys', 'hr',
            'iframe', 'image', 'image-processor', 'image-properties', 'indent',
            'inline-popup', 'justify', 'key-arrow-outside', 'limit', 'line-height',
            'link', 'media', 'mobile', 'ordered-list', 'paste', 'paste-from-word',
            'paste-storage', 'placeholder', 'powered-by-jodit', 'preview', 'print',
            'redo-undo', 'resize-cells', 'resize-handler', 'resizer', 'search',
            'select', 'select-cells', 'size', 'source', 'speech-recognize',
            'spellcheck', 'stat', 'sticky', 'symbols', 'tab', 'table',
            'table-keyboard-navigation', 'video', 'wrap-nodes', 'xpath'
        ],
        lists: {
            indent: true,
            startIndent: 20,
        },
    }), []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setServiceData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
    
        try {
            const submissionData = {
                title: serviceData.title,
                link: serviceData.link,
                intlink: serviceData.intlink,  // Make sure this is included
                pagedetails: content,
                icon: serviceData.icon
            };
    
            const response = await axios.put(`${Base_Url}/howto/${id}`, submissionData);
    
            if (response.status === 200 || response.status === 204) {
                setSuccess('HowTo details updated successfully!');
                setTimeout(() => {
                    navigate('/howto');
                }, 2000);
            }
        } catch (err) {
            console.error('Update Error:', err);
            setError(err.response?.data?.error || 'Failed to update service');
        }
    };

    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-header">
                            <h2 className="text-center">Edit HowTo Content</h2>
                        </div>
                        <div className="card-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="alert alert-success" role="alert">
                                    {success}
                                </div>
                            )}
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Card Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        placeholder="Your Card Name"
                                        value={serviceData.title}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">External Link (Optional)</label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="link"
                                        value={serviceData.link}
                                        onChange={handleChange}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Route Link (Optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="intlink"
                                        value={serviceData.intlink}
                                        onChange={handleChange}
                                        placeholder="example : /services"
                                    />
                                </div>

                                {/* <div className="mb-3">
                                    <label className="form-label">Page Details</label>
                                    <JoditEditor
                                        ref={editor}
                                        value={content}
                                        config={editorConfig}
                                        tabIndex={1}
                                        onBlur={(newContent) => setContent(newContent)}
                                        onChange={() => {}}
                                    />
                                </div> */}

                                {/* <div className="mb-3">
                                    <label className="form-label">Icon</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="icon"
                                        value={serviceData.icon}
                                        onChange={handleChange}
                                        placeholder="Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code"
                                        autoComplete="off"
                                    />
                                    <a href="https://lucide.dev/icons/" target="_blank" rel="noopener noreferrer">
                                        <button type="button" className="btn btn">
                                            <span className="badge badge-light">
                                                Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code
                                            </span>
                                        </button>
                                    </a>
                                </div> */}

                                <div className="text-center">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg"
                                    >
                                        Update
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAdvancedServiceCard;